import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * AppDrawerContext
 * This Context provides state and actions for managing application drawers that need to transition seamlessly between features.
 * Use with the AppDrawer component to give the appearance that one drawer is rendered and only the contents of the drawer is changing.
 */

type AppDrawerContextType = {
  activeAppDrawer: string | null;
  activeAppDrawerBeforeOpenRef: React.MutableRefObject<string | null>;
  closeAppDrawer: () => void;
  isAppDrawerOpen: boolean;
  openAppDrawer: (id: string) => void;
};

export const AppDrawerContext = createContext<AppDrawerContextType | undefined>(
  undefined
);

export const AppDrawerProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [activeAppDrawer, setActiveAppDrawer] = useState<string | null>(null);
  const isAppDrawerOpen = Boolean(activeAppDrawer);
  const activeAppDrawerBeforeOpenRef = React.useRef<string | null>(null);

  // The isMountedRef is used to prevent state updates on an unmounted component,
  // which can cause React memory leaks.
  const isMountedRef = React.useRef(true);

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const openAppDrawer = useCallback(
    (id: string) => {
      activeAppDrawerBeforeOpenRef.current = activeAppDrawer;
      if (isMountedRef.current) {
        setActiveAppDrawer(id);
      }
    },
    [activeAppDrawer]
  );
  const closeAppDrawer = useCallback(() => {
    if (isMountedRef.current) {
      setActiveAppDrawer(null);
    }
    activeAppDrawerBeforeOpenRef.current = null;
  }, []);

  return (
    <AppDrawerContext.Provider
      value={{
        activeAppDrawer,
        activeAppDrawerBeforeOpenRef,
        closeAppDrawer,
        isAppDrawerOpen,
        openAppDrawer
      }}
    >
      {children}
    </AppDrawerContext.Provider>
  );
};

export const useAppDrawer = () => {
  const context = useContext(AppDrawerContext);
  if (!context)
    throw new Error('useAppDrawer must be used within an AppDrawerProvider');
  return context;
};
